/**
 * V111: MM 등록 페이지
 * V113.1: enabled 기반 표시 (getMmOverviewData에서 필터링)
 *
 * 팀별 MM 현황을 조회하고 프로젝트별 메모를 등록합니다.
 * - 인력현황 (전체인력, 투입인력, 유휴인력, 중복투입)
 * - 프로젝트별 MD 현황 + 메모 편집
 * - 실시간 계산 (cutoff 없음)
 *
 * V113.1: mmOrgSettings.enabled=true인 조직만 목록에 표시
 * - enabled=false → 작성 UI 비노출 (getMmOverviewData에서 필터링)
 * - 저장은 enabled와 무관하게 가능 (BE에서 차단하지 않음)
 *
 * @route /mm/register
 * @access role_manager, role_exec, admin, TL
 */

import React, { useCallback, useMemo, useState } from 'react';
import { Loader, AlertCircle } from 'lucide-react';
import { useMmOverviewData } from '../../shared/hooks/useMmOverviewData';
import { useAuthContext } from '../../shared/hooks/useAuthContext';
import { useMmProjectNoteMutation } from '../../shared/hooks/useMmProjectNoteMutation';
import { MmMonthSelector } from './components/MmMonthSelector';
import { MmTeamSummaryCard } from './components/MmTeamSummaryCard';
import { MmProjectTable } from './components/MmProjectTable';
import { getCurrentMonth, formatMonthKo } from '../../shared/types/mm';

/**
 * MM 등록 페이지
 */
export function MMRegisterPage() {
  // 월 선택 상태
  const [month, setMonth] = React.useState(getCurrentMonth());

  // V314: 통합어드민 국가 선택
  const authContext = useAuthContext();
  const hasGlobalScopeAccess = useMemo(() => {
    const roleIds = authContext?.roleAssignments?.map((ra) => ra.roleId) || [];
    return roleIds.includes('system_admin') || roleIds.includes('role_global');
  }, [authContext?.roleAssignments]);
  const [filterCompanyCode, setFilterCompanyCode] = useState<'KR' | 'CN'>('KR');

  // V60.x: 회사 코드 — BE가 사용자 역할 기반으로 확정 (KR 하드코딩 제거)
  // MM 현황 데이터 조회
  const { data, isLoading, error, refetch, resolvedCompanyCode } = useMmOverviewData({
    month,
    // V314: 통합어드민은 선택한 국가 전달
    ...(hasGlobalScopeAccess && { companyCode: filterCompanyCode }),
    // V73.3: MM작성 → KR role_manager 스코프 SUBTREE 유지
    viewMode: 'register',
  });

  // 메모 저장 mutation
  const { saveNote, isSaving } = useMmProjectNoteMutation();

  // V130: 메모 저장 핸들러 (개별 필드 patch 저장 지원)
  const handleSaveNote = useCallback(
    async (
      teamId: string,
      projectId: string,
      notes: { workDetails?: string; remarks?: string; costOccurred?: string }
    ) => {
      await saveNote({
        teamId,
        projectId,
        month,
        // V130: 제공된 필드만 전달 (undefined 필드는 BE에서 무시됨)
        ...(notes.workDetails !== undefined && { workDetails: notes.workDetails }),
        ...(notes.remarks !== undefined && { remarks: notes.remarks }),
        ...(notes.costOccurred !== undefined && { costOccurred: notes.costOccurred }),
      });

      // 저장 성공 시 데이터 새로고침
      await refetch();
    },
    [month, saveNote, refetch]
  );

  return (
    <div style={{ padding: '24px' }}>
      {/* 헤더 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px',
        }}
      >
        <h1
          style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: 'var(--color-gray-800)',
          }}
        >
          MM 등록
        </h1>

        {/* V314: 통합어드민 국가 선택 */}
        {hasGlobalScopeAccess && (
          <select
            value={filterCompanyCode}
            onChange={(e) => setFilterCompanyCode(e.target.value as 'KR' | 'CN')}
            style={{
              padding: '6px 12px',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: 'white',
            }}
          >
            <option value="KR">KR (한국)</option>
            <option value="CN">CN (대련)</option>
          </select>
        )}
      </div>

      {/* 월 선택기 */}
      <MmMonthSelector
        month={month}
        onMonthChange={setMonth}
        workingDays={data?.monthInfo.workingDays}
        disabled={isLoading}
      />

      {/* 안내 메시지 */}
      <div
        style={{
          padding: '12px 16px',
          backgroundColor: 'var(--color-blue-50)',
          borderRadius: '8px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '13px',
          color: 'var(--color-blue-700)',
        }}
      >
        <AlertCircle size={16} />
        <span>
          각 프로젝트의 업무 상세내역, 특이사항, 테스트 결제비용 항목을 입력한 후 저장
          버튼을 클릭하세요.
        </span>
      </div>

      {/* 로딩 */}
      {isLoading && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '48px',
            color: 'var(--color-gray-500)',
          }}
        >
          <Loader
            size={24}
            style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }}
          />
          <span>{formatMonthKo(month)} MM 데이터 로딩 중...</span>
        </div>
      )}

      {/* 에러 */}
      {error && (
        <div
          style={{
            padding: '16px',
            backgroundColor: 'var(--color-red-50)',
            color: 'var(--color-red-700)',
            borderRadius: '8px',
            marginBottom: '16px',
          }}
        >
          데이터 조회 중 오류가 발생했습니다: {error.message}
        </div>
      )}

      {/* 팀별 데이터 */}
      {data && !isLoading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {data.teams.length === 0 ? (
            <div
              style={{
                padding: '48px',
                textAlign: 'center',
                color: 'var(--color-gray-500)',
                backgroundColor: 'var(--color-gray-50)',
                borderRadius: '8px',
              }}
            >
              등록된 팀이 없습니다.
            </div>
          ) : (
            data.teams.map((team) => (
              <div
                key={team.teamId}
                style={{
                  marginBottom: '32px',
                  padding: '20px',
                  backgroundColor: 'white',
                  border: '1px solid var(--color-gray-200)',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                }}
              >
                {/* 인력현황 카드 */}
                <MmTeamSummaryCard team={team} contextCompanyCode={resolvedCompanyCode} />

                {/* V116.1.6: 프로젝트 테이블 - MMOverviewPage와 동일 구조 */}
                <div
                  style={{
                    backgroundColor: 'var(--color-gray-50)',
                    borderRadius: '8px',
                    padding: '16px',
                  }}
                >
                  {/* V207: monthWorkingDays 전달 (WD 기반 MM 환산) */}
                  <MmProjectTable
                    projects={team.projects}
                    teamId={team.teamId}
                    month={month}
                    isEditable={true}
                    onSaveNote={handleSaveNote}
                    isSaving={isSaving}
                    monthWorkingDays={data?.monthInfo.workingDays}
                  />
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* 저장 중 오버레이 */}
      {isSaving && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '24px 32px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            }}
          >
            <Loader
              size={20}
              style={{ animation: 'spin 1s linear infinite' }}
            />
            <span>저장 중...</span>
          </div>
        </div>
      )}

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
